import { useCallback, useEffect, useState } from 'react';
import { Box, Skeleton } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { useDispatch, useSelector } from 'react-redux';
import { differenceInCalendarDays, format, parseISO, subDays } from 'date-fns';
import { useLocation } from 'react-router';
import { SplashScreen } from 'src/components/loading-screen';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import { CountryBar } from 'src/components/country-bar';
import { CountryItem } from 'src/components/country-bar/types';
import { useCountryData } from 'src/components/visibility/hooks/useCountryData';
import { HEADER } from 'src/layouts/config-layout';
import { CountryTabLabel } from '../explore/CountryTabLabel';
import { usePersistedCountries } from '../explore/usePersistedCountries';
import {
  setDatesForcedByActivation,
  setFilterCompare,
  setFilterCountry,
  setFilterDate,
  setFilterDateCompare,
  setFilterMinDatePicker,
  setFilterTimestep,
  setSearchPerfDateOverridden,
} from 'src/context/reducers/filter-settings/filterSlice';
import { getDateRangeIso, getLast12WeeksDateRange } from 'src/utils/format-time';
// import FunnelKpiCards from './FunnelKpiCards';
import { useFunnelKpis } from './hooks/useFunnelKpis';
import FunnelDistributionChart from './FunnelDistributionChart';
import FunnelPerformanceChart from './FunnelPerformanceChart';
// import ConversionsAnalysis from '../conversionsAnalysis';
import { PAGE_PADDING_X, SECTION_GAP } from './constants';
import KpiToggle, { useKPIToggle } from '../explore/ExploreKpiCards';
import ExploreTable from '../explore/ExploreTable';
export default function FunnelView() {
  const dispatch = useDispatch();
  const filterStore = useSelector((state: any) => state.filters);
  const workspace: any = useCurrentWorkspace();
  const location = useLocation();
  const [selectedDisplayGraph, setSelectedDisplayGraphValue] = useState('Value');
  const [tableEverLoaded, setTableEverLoaded] = useState(false);
  const toggle = useKPIToggle(
    filterStore.extraOptions?.mode === 'activations'
      ? ['ad_brand_traffic', 'cpc']
      : ['brand_searches', 'total_brand_traffic']
  );
  // Countries + coverage % via the Visibility query (front-only), same as Explore.
  const { countriesData: countries, isLoading: isLoadingCountries } = useCountryData();
  // List FROZEN on first load, same as Explore.
  const [countryList, setCountryList] = useState<CountryItem[]>([]);
  const loadingTop = toggle.isLoadingStats || toggle.isLoadingCompareStats || !toggle.statsData;
  useEffect(() => {
    if (countryList.length === 0 && countries.length > 0) {
      setCountryList(countries);
    }
  }, [countries, countryList.length]);
  const handleRestore = useCallback(
    (names: string[]) => {
      dispatch(setFilterCountry(names));
    },
    [dispatch]
  );
  const { persist } = usePersistedCountries(countryList, handleRestore);
  const handleCountriesChange = (names: string[]) => {
    dispatch(setFilterCountry(names));
    persist(names);
  };
  const allCountryNames = countryList.map((c) => c.country_name);
  const {
    mainData: kpisData,
    compareData,
    error: kpisError,
    isFetching: isFetchingKpis,
  } = useFunnelKpis(filterStore?.countries ?? [], allCountryNames, workspace);
  useEffect(() => {
    if (kpisData?.min_date) {
      dispatch(setFilterMinDatePicker(kpisData.min_date));
    }
  }, [kpisData?.min_date, dispatch]);
  useEffect(() => {
    if (!workspace?.id) return;
    const { start, end } = getLast12WeeksDateRange();
    const startDate = parseISO(start);
    const endDate = parseISO(end);
    const periodDuration =
      differenceInCalendarDays(endDate, startDate) + 1;
    const compareEnd = subDays(startDate, 1);
    const compareStart = subDays(compareEnd, periodDuration - 1);
    // Funnel must not inherit the Overview "since activation" date mode.
    dispatch(setDatesForcedByActivation(false));
    // Funnel takes control of its own date range when entering the page.
    dispatch(setSearchPerfDateOverridden(false));
    // Set the timestep first and disable automatic alignment here because
    // the exact current and comparison ranges are defined just below.
    dispatch(
      setFilterTimestep({
        timestep: 'year_week',
        skipAlignment: true,
      })
    );
    dispatch(
      setFilterDate({
        start: format(startDate, 'yyyy-MM-dd'),
        end: format(endDate, 'yyyy-MM-dd'),
        skipAlignment: true,
      })
    );
    dispatch(
      setFilterDateCompare({
        compareStart: format(compareStart, 'yyyy-MM-dd'),
        compareEnd: format(compareEnd, 'yyyy-MM-dd'),
        skipAlignment: true,
      })
    );
    dispatch(setFilterCompare(true));
  }, [dispatch, workspace?.id]);
  useEffect(() => {
    if (workspace?.name?.includes('Demo')) {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const { start, end } = getDateRangeIso(
        new Date(2024, 8, 1),
        new Date(2024, 10, 30),
        tz
      );
      dispatch(
        setFilterTimestep({
          timestep: 'date_day',
          skipAlignment: true,
        })
      );
      dispatch(
        setFilterDate({
          start,
          end,
          skipAlignment: true,
        })
      );
    }
  }, [dispatch, workspace?.id, workspace?.name, location?.pathname]);
  useEffect(() => {
    if (!loadingTop) {
      setTableEverLoaded(true);
    }
  }, [loadingTop]);
  if (!filterStore?.countries || !filterStore?.keywords || !filterStore?.devices) {
    return <SplashScreen />;
  }
  return (
    <Box px="24px">
      <CountryBar
        countries={countryList}
        multiple
        selectedCountries={filterStore.countries ?? []}
        onCountriesChange={handleCountriesChange}
        loading={isLoadingCountries || (countryList.length === 0 && isFetchingKpis)}
        stickyTop={HEADER.H_DESKTOP * 3 + 6}
        backgroundColor="background.paper"
        topGap={60}
        reserveGapWhenHidden
        contentPaddingX={PAGE_PADDING_X}
        tabSpacing={12}
        renderCountryLabel={(c, isSelected) => (
          <CountryTabLabel country={c} isSelected={isSelected} />
        )}
      />
      {loadingTop ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(5, minmax(0, 1fr)) 40px',
            gap: '4px',
            width: '100%',
            mb: '24px',
          }}
        >
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton
              key={index}
              width="100%"
              height={100}
              variant="rectangular"
              animation="wave"
              sx={{
                borderRadius: 0,
                borderTopLeftRadius: index === 0 ? '12px' : 0,
                borderBottomLeftRadius: index === 0 ? '12px' : 0,
              }}
            />
          ))}
          <Skeleton
            width="100%"
            height={100}
            variant="rectangular"
            animation="wave"
            sx={{
              borderTopRightRadius: '12px',
              borderBottomRightRadius: '12px',
            }}
          />
        </Box>
      ) : (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            mb: '24px',
          }}
        >
          <KpiToggle {...toggle} />
        </Box>
      )}
      <Box sx={{ marginBottom: '24px' }}>
        <Grid container spacing={SECTION_GAP}>
          <Grid xs={12} md={6}>
            <FunnelDistributionChart
              kpisData={kpisData}
              compareData={compareData}
              kpisError={kpisError}
              isFetchingKpis={isFetchingKpis}
              selectedDisplayGraph={selectedDisplayGraph}
              setSelectedDisplayGraphValue={setSelectedDisplayGraphValue}
            />
          </Grid>
          <Grid xs={12} md={6}>
            <FunnelPerformanceChart
              kpisData={kpisData}
              compareData={compareData}
              kpisError={kpisError}
              isFetchingKpis={isFetchingKpis}
            />
          </Grid>
        </Grid>
      </Box>
      {loadingTop && !tableEverLoaded ? (
        <Skeleton
          variant="rectangular"
          animation="wave"
          width="100%"
          height={403}
          sx={{
            borderRadius: 2,
            mt: 2,
          }}
        />
      ) : (
        <ExploreTable />
      )}
    </Box>
  );
}